import { useState, useEffect } from "react";
import backendApi from "../api/backendApi.js";
import { useAuth } from "../api/AuthContext";
import LoadingButton from "./LoadingButton";
import AlertModal from "../components/modal/AlertModal";
import { protectedTheme } from "./ui/protectedTheme";

const FPX_FLAT_FEE = 1.0;
const QR_RATE_PER_THOUSAND = 16; // 1.60%
const QR_MIN = 0.15; // RM0.15 minimum

const n = (v) => {
  const x = parseFloat(v);
  return isNaN(x) ? 0 : x;
};

const round2 = (v) => Math.round((v + Number.EPSILON) * 100) / 100;
const ceilDiv = (num, den) => Math.floor((num + den - 1) / den);
const calculateKhuddamAmounts = (monthlySalary) => ({
  membership: round2(n(monthlySalary) * 0.005),
  ijtima: round2(n(monthlySalary) * 0.015),
});

// Gross-up fee so the provider fee is covered by the gross amount.
const calcQrFeeGrossUp = (
  subtotalRM,
  ratePerThousand = QR_RATE_PER_THOUSAND,
  minRM = QR_MIN
) => {
  if (!subtotalRM || subtotalRM <= 0) return 0;
  const minSen = Math.round(minRM * 100);
  const subSen = Math.round(subtotalRM * 100);

  let totalSen = subSen;
  for (let i = 0; i < 10; i++) {
    const feeSenRaw = ceilDiv(totalSen * ratePerThousand, 1000);
    const feeSen = Math.max(minSen, feeSenRaw);
    const newTotalSen = subSen + feeSen;
    if (newTotalSen === totalSen) {
      return feeSen / 100;
    }
    totalSen = newTotalSen;
  }
  const feeSenRaw = ceilDiv(totalSen * ratePerThousand, 1000);
  return Math.max(minSen, feeSenRaw) / 100;
};

const BayarCashForm = () => {
  const { user } = useAuth();
  const tanzeem = (user?.tanzeem || "KHUDDAM").toUpperCase();

  // Modes
  const [mode, setMode] = useState(tanzeem === "ATHFAL" ? "manual" : "salary");

  const [salary, setSalary] = useState("");
  const [khuddamMembership, setKhuddamMembership] = useState(0);
  const [khuddamIjtima, setKhuddamIjtima] = useState(0);

  const [athfalMembership, setAthfalMembership] = useState(
    tanzeem === "ATHFAL" ? 2 : 0
  );
  const [athfalIjtima, setAthfalIjtima] = useState(
    tanzeem === "ATHFAL" ? 5 : 0
  );

  const [hall, setHall] = useState(0);
  const [localFund, setLocalFund] = useState(0);
  const [khidmatKhalaq, setKhidmatKhalaq] = useState(0);
  const [paymentMonth, setPaymentMonth] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("1"); // 1=FPX, 6=DuitNow QR
  const [transactionFee, setTransactionFee] = useState(1.0);
  const [total, setTotal] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [showQrWarning, setShowQrWarning] = useState(false);

  // Active fields based on tanzeem
  const activeMembership =
    tanzeem === "ATHFAL" ? athfalMembership : khuddamMembership;
  const activeIjtima = tanzeem === "ATHFAL" ? athfalIjtima : khuddamIjtima;

  useEffect(() => {
    const optionalSum = n(hall) + n(localFund) + n(khidmatKhalaq);
    const baseSum = n(activeMembership) + n(activeIjtima);
    const subtotal = baseSum + optionalSum;

    if (paymentMethod === "6") {
      // DuitNow QR: 1.60% of GROSS, min RM0.15, rounded up
      const fee = calcQrFeeGrossUp(subtotal);
      setTransactionFee(round2(fee));
      setTotal(round2(subtotal + fee));
    } else {
      // FPX or others: flat RM1 (adjust if you add other rules)
      setTransactionFee(FPX_FLAT_FEE);
      setTotal(round2(subtotal + FPX_FLAT_FEE));
    }
  }, [
    activeMembership,
    activeIjtima,
    hall,
    localFund,
    khidmatKhalaq,
    paymentMethod,
  ]);

  useEffect(() => {
    if (tanzeem !== "KHUDDAM" || mode !== "salary") return;
    if (salary !== "") return;
    if (user?.monthlySalary === null || user?.monthlySalary === undefined) {
      return;
    }

    const savedSalary = n(user.monthlySalary);
    const amounts = calculateKhuddamAmounts(savedSalary);
    setSalary(savedSalary);
    setKhuddamMembership(amounts.membership);
    setKhuddamIjtima(amounts.ijtima);
  }, [mode, salary, tanzeem, user?.monthlySalary]);

  // Salary autofill/autosave source (Khuddam only).
  const handleSalaryInput = (e) => {
    const monthlySalary = e.target.value === "" ? "" : n(e.target.value);
    const amounts = calculateKhuddamAmounts(monthlySalary);
    setSalary(monthlySalary);
    setKhuddamMembership(amounts.membership);
    setKhuddamIjtima(amounts.ijtima);
  };

  const handlePaymentMethodChange = (value) => {
    setPaymentMethod(value);
    // if (value === "6") {
    //   setShowQrWarning(true);
    // }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payments = [];

    if (tanzeem === "ATHFAL") {
      payments.push(
        { category: "ATHFAL_MEMBERSHIP", amount: n(athfalMembership) },
        { category: "ATHFAL_IJTIMA", amount: n(athfalIjtima) }
      );
    } else {
      payments.push(
        { category: "KHUDDAM_MEMBERSHIP", amount: n(khuddamMembership) },
        { category: "KHUDDAM_IJTIMA", amount: n(khuddamIjtima) }
      );
    }

    // Optional categories only if > 0
    if (n(hall) > 0) payments.push({ category: "HALL", amount: n(hall) });
    if (n(localFund) > 0)
      payments.push({ category: "LOCAL_FUND", amount: n(localFund) });
    if (n(khidmatKhalaq) > 0)
      payments.push({ category: "KHIDMAT_KHALAQ", amount: n(khidmatKhalaq) });

    // Always include transaction fee as its own line
    payments.push({ category: "TRX_FEE", amount: n(transactionFee) });

    const payload = {
      payment_channel: paymentMethod,
      order_number: `TRX${Date.now()}`,
      amount: total,
      payer_email: user.email,
      paymentMonth,
      payments,
    };

    if (tanzeem === "KHUDDAM" && mode === "salary") {
      payload.monthlySalary = round2(n(salary));
    }

    setSubmitting(true);
    try {
      const res = await backendApi.post("/payment/intent", payload);
      const data = res.data;
      if (data.url) {
        window.location.href = data.url;
      } else {
        alert("An error has occured. Please try again later.");
        setSubmitting(false);
      }
    } catch (err) {
      console.error(err);
      alert("An error has occured. Please try again later.");
      setSubmitting(false);
    }
  };

  // Labels & bindings
  const membershipLabel =
    tanzeem === "ATHFAL" ? "Athfal Membership (RM)" : "Khuddam Membership (RM)";
  const ijtimaLabel =
    tanzeem === "ATHFAL" ? "Athfal Ijtima (RM)" : "Khuddam Ijtima (RM)";
  const membershipValue = activeMembership;
  const ijtimaValue = activeIjtima;

  const setMembership =
    tanzeem === "ATHFAL"
      ? (v) => setAthfalMembership(n(v))
      : (v) => setKhuddamMembership(n(v));
  const setIjtima =
    tanzeem === "ATHFAL"
      ? (v) => setAthfalIjtima(n(v))
      : (v) => setKhuddamIjtima(n(v));

  return (
    <form
      onSubmit={handleSubmit}
      className={`${protectedTheme.page} flex flex-col items-center`}
    >
      <div className="mx-auto w-full max-w-5xl space-y-5">
        <div className="rounded-3xl bg-emerald-950 p-5 text-white shadow-lg">
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-lime-200">
            Payment
          </p>
          <h2 className="mt-2 text-3xl font-black tracking-tight">
            Pay{" "}
            {tanzeem
              .toLowerCase()
              .split()
              .map((w) => w.charAt(0).toUpperCase() + w.slice(1))}{" "}
            Chanda
          </h2>
          <p className="mt-2 max-w-2xl text-sm text-emerald-100">
            Choose the reporting month, confirm your chanda categories, and
            continue securely through BayarCash.
          </p>
        </div>

        {/* Khuddam only: salary/manual toggle */}
        {tanzeem === "KHUDDAM" && (
          <div className="flex flex-col gap-2 rounded-2xl bg-white p-2 shadow sm:flex-row">
            <button
              type="button"
              className={`rounded-xl px-4 py-3 text-sm font-bold transition ${
                mode === "salary"
                  ? "bg-lime-300 text-emerald-950 shadow"
                  : "bg-white text-zinc-600 hover:bg-emerald-50"
              }`}
              onClick={() => {
                setMode("salary");
                const restoredSalary =
                  salary !== "" ? salary : user?.monthlySalary;
                if (
                  restoredSalary !== undefined &&
                  restoredSalary !== null &&
                  restoredSalary !== ""
                ) {
                  const monthlySalary = n(restoredSalary);
                  const amounts = calculateKhuddamAmounts(monthlySalary);
                  setSalary(monthlySalary);
                  setKhuddamMembership(amounts.membership);
                  setKhuddamIjtima(amounts.ijtima);
                }
              }}
            >
              Based on Salary
            </button>
            <button
              type="button"
              className={`rounded-xl px-4 py-3 text-sm font-bold transition ${
                mode === "manual"
                  ? "bg-lime-300 text-emerald-950 shadow"
                  : "bg-white text-zinc-600 hover:bg-emerald-50"
              }`}
              onClick={() => {
                setMode("manual");
                setKhuddamMembership(0);
                setKhuddamIjtima(0);
              }}
            >
              Manual Input
            </button>
          </div>
        )}

        <section className={protectedTheme.card}>
          <label className={protectedTheme.label}>Payment For Month</label>
          <input
            type="month"
            value={paymentMonth}
            onChange={(e) => setPaymentMonth(e.target.value)}
            required
            className={`mt-2 ${protectedTheme.input}`}
          />
        </section>

        {/* Salary input only for Khuddam in salary mode */}
        {tanzeem === "KHUDDAM" && mode === "salary" && (
          <section className={protectedTheme.card}>
            <label className={protectedTheme.label}>Monthly Salary (RM)</label>
            <input
              type="number"
              value={salary}
              onChange={handleSalaryInput}
              onFocus={(e) => e.target.select()}
              className={`mt-2 ${protectedTheme.input}`}
              placeholder="0"
              min="0"
              step="0.01"
              required
            />
          </section>
        )}

        {/* Membership */}
        <section className={protectedTheme.card}>
          <label className={protectedTheme.label}>{membershipLabel}</label>
          <input
            type="number"
            value={membershipValue}
            onChange={(e) => setMembership(e.target.value)}
            onFocus={(e) => e.target.select()}
            className={`mt-2 ${protectedTheme.input}`}
            disabled={tanzeem === "KHUDDAM" && mode === "salary"}
            placeholder="0"
            min="0"
            step="0.01"
            required
          />
        </section>

        {/* Ijtima */}
        <section className={protectedTheme.card}>
          <label className={protectedTheme.label}>{ijtimaLabel}</label>
          <input
            type="number"
            value={ijtimaValue}
            onChange={(e) => setIjtima(e.target.value)}
            onFocus={(e) => e.target.select()}
            className={`mt-2 ${protectedTheme.input}`}
            disabled={tanzeem === "KHUDDAM" && mode === "salary"}
            placeholder="0"
            min="0"
            step="0.01"
            required
          />
        </section>

        {/* extra categories */}
        <section className={protectedTheme.card}>
          <h3 className="text-lg font-black text-zinc-950">
            Optional Contributions
          </h3>
          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
          <div>
            <label className={protectedTheme.label}>Khuddam Hall (RM)</label>
            <input
              type="number"
              value={hall}
              onChange={(e) => setHall(n(e.target.value))}
              onFocus={(e) => e.target.select()}
              className={`mt-1 ${protectedTheme.input}`}
              placeholder="0"
              min="0"
              step="0.01"
            />
          </div>
          <div>
            <label className={protectedTheme.label}>Local Fund (RM)</label>
            <input
              type="number"
              value={localFund}
              onChange={(e) => setLocalFund(n(e.target.value))}
              onFocus={(e) => e.target.select()}
              className={`mt-1 ${protectedTheme.input}`}
              placeholder="0"
              min="0"
              step="0.01"
            />
          </div>
          <div>
            <label className={protectedTheme.label}>Khidmat-e-Khalaq (RM)</label>
            <input
              type="number"
              value={khidmatKhalaq}
              onChange={(e) => setKhidmatKhalaq(n(e.target.value))}
              onFocus={(e) => e.target.select()}
              className={`mt-1 ${protectedTheme.input}`}
              placeholder="0"
              min="0"
              step="0.01"
            />
          </div>
          </div>
        </section>

        {/* Payment Method */}
        <section className={protectedTheme.card}>
          <label className={`${protectedTheme.label} mb-2`}>
            Payment Method{" "}
            <span className="text-xs font-[400]">(Choose one)</span>
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              {
                label: "FPX",
                value: "1",
                image: "/logos/FPX_Logo.png",
                ewallet: [
                  "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRgK2nHLE9fyCC-I_8VgaCVjssA3QCInEApnA&s",
                  "https://play-lh.googleusercontent.com/6QI_2DDtZwqZrDNUQmZZuWf_9tBUnWpmxTwn55056yrNKo4_vPcXWZPES2aYQecfPg=w240-h480-rw",
                  "https://play-lh.googleusercontent.com/0V5acn1KdUAdvWtCmS84lTCu2wCMx4mJP91CyP2va9QW3W-YNwC4eudvtB22UDnQ4N0=w240-h480-rw",
                  "https://play-lh.googleusercontent.com/J5b8CTbM2G8mM95q90NaBgC0yBCwbToyS-UCOQ61ON5X034gKr9prcO_xgf6QmLnR14",
                  "https://play-lh.googleusercontent.com/2BB7EX-3t64V1REwoOFvJhr-jsVjf4ApV4WArwJYbQC9jk4xlWjibcAT3b89eRORPII",
                ],
              },
              {
                label: "DuitNow QR",
                value: "6",
                image: "/logos/DuitNow_QR_Logo.png",
                ewallet: [
                  "https://assets-global.website-files.com/604f10ece77ed2575179c749/60c14f09516c063c7ee9620a_tngd-logo.svg",
                  "https://play-lh.googleusercontent.com/jisrrJI1g6BeqGrM_0_IlRsK3LFNOtTnY-U7XPQlyKIgdF1sFtFWrlOPiKlyG79tZiEd",
                  "https://play-lh.googleusercontent.com/s1HFaz0W0b_7WXPrOKyq6nhIN_DLnEDRFkw0VhDR-NnU9gBs0j_LZbaC01eqOD-F2up_B-tnzGT0UxVygYRltA",
                  "https://play-lh.googleusercontent.com/pqL8IExLsVtbD7FWTcCRoxfLsNIyO1WwszEg3vZJWaB3lhxoOoIshpGaiFl5z-aP8Cc",
                  "https://assets.grab.com/wp-content/uploads/sites/8/2021/11/26235239/GrabPay_Final_Logo_RGB_green_StackedVersion-01.png",
                  "https://play-lh.googleusercontent.com/lYJv1PtBc7EYYjG1mcY5hIlrnE8Gmewjd5T8AHEmh0SYR5XHqJpx_pWMvXSRYq4cobli=w240-h480-rw",
                ],
              },
            ].map((method) => (
              <label
                key={method.value}
                className={`relative flex items-center rounded-xl border p-3 pl-4 text-xs font-bold transition md:justify-center ${
                  paymentMethod === method.value
                    ? "border-emerald-700 bg-emerald-50 ring-2 ring-lime-200"
                    : "border-zinc-200 bg-white hover:border-emerald-200"
                }`}
              >
                <input
                  type="radio"
                  value={method.value}
                  checked={paymentMethod === method.value}
                  onChange={(e) => handlePaymentMethodChange(e.target.value)}
                  className="mr-2 hidden"
                  required
                />
                <img
                  src={method.image}
                  alt={method.label}
                  className="h-6 mr-3"
                />
                <span>{method.label}</span>
                <div className="flex absolute right-0 mr-2 space-x-1">
                  {method.ewallet.map((url, index) => (
                    <img
                      key={index}
                      src={url}
                      alt="ewallet"
                      className="h-6 rounded-lg"
                    />
                  ))}
                </div>
              </label>
            ))}
          </div>
        </section>

        {/* Summary */}
        <section className={`${protectedTheme.card} flex flex-col gap-3`}>
          <h3 className="text-lg font-black text-zinc-950">
            Payment Summary
          </h3>
          {n(membershipValue) > 0 && (
            <p className="flex justify-between text-sm text-zinc-600">
              {membershipLabel.replace(" (RM)", "")}:{" "}
              <strong>RM {n(membershipValue).toFixed(2)}</strong>
            </p>
          )}
          {n(ijtimaValue) > 0 && (
            <p className="flex justify-between text-sm text-zinc-600">
              {ijtimaLabel.replace(" (RM)", "")}:{" "}
              <strong>RM {n(ijtimaValue).toFixed(2)}</strong>
            </p>
          )}
          {n(hall) > 0 && (
            <p className="flex justify-between text-sm text-zinc-600">
              Khuddam Hall: <strong>RM {n(hall).toFixed(2)}</strong>
            </p>
          )}
          {n(localFund) > 0 && (
            <p className="flex justify-between text-sm text-zinc-600">
              Local Fund: <strong>RM {n(localFund).toFixed(2)}</strong>
            </p>
          )}
          {n(khidmatKhalaq) > 0 && (
            <p className="flex justify-between text-sm text-zinc-600">
              Khidmat-e-Khalaq:{" "}
              <strong>RM {n(khidmatKhalaq).toFixed(2)}</strong>
            </p>
          )}
          <p className="flex justify-between text-sm text-zinc-600">
            Transaction Fee: <strong>RM {transactionFee.toFixed(2)}</strong>
          </p>
          <hr className="border-zinc-200" />
          <p className="flex justify-between text-lg text-zinc-950">
            Total to Pay: <strong>RM {total.toFixed(2)}</strong>
          </p>
        </section>

        <LoadingButton type="submit" loading={submitting}>
          Pay Now
        </LoadingButton>
      </div>

      <AlertModal
        isOpen={showQrWarning}
        onClose={() => setShowQrWarning(false)}
        isOneButton
        onSuccessOneButton={() => setShowQrWarning(false)}
        title="You have selected DuitNow QR payment method"
        isQrSelected={true}
        message={
          <>
            <p className="mb-2">
              Sometimes QR may fail to generate. Here are what you shoud do:
            </p>
            <ol className="list-decimal list-inside mb-2 text-left">
              <li>Do not close the payment page.</li>
              <li>
                After <strong>5 minutes</strong> click on{" "}
                <strong>'Regenerate QR'</strong>
              </li>
              <li>
                If you accidentally close the page, you may reopen it via:
                <br />
                <code className="text-center border bg-slate-200">
                  Dashboard → transaction with status <strong>INITIATED</strong>{" "}
                  → Pay Now
                </code>
              </li>
            </ol>
            <p className="mt-2">
              ⏱ You have 20 minutes before the payment expires.
            </p>
          </>
        }
        confirmText="Okay"
      />
    </form>
  );
};

export default BayarCashForm;
